/**
 * Shared cookie utilities for cross-service theme synchronization
 * Used by both apps/web and apps/cms
 */

// Cookie name as per AC #4 - shared across all services
export const THEME_COOKIE_NAME = "theme-preference";

export type Theme = "light" | "dark" | "system";

// Escape regex special characters in cookie name
function escapeRegex(str: string): string {
	return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getCookie(name: string): string | null {
	if (typeof document === "undefined") {
		return null;
	}
	const escapedName = escapeRegex(name);
	const match = document.cookie.match(
		new RegExp(`(^| )${escapedName}=([^;]+)`),
	);
	return match?.[2] ?? null;
}

export function setCookie(name: string, value: string, days = 365): void {
	if (typeof document === "undefined") {
		return;
	}
	const maxAge = days * 24 * 60 * 60;
	const isSecure = window.location.protocol === "https:";
	// SameSite=Lax for cross-service compatibility, path=/ for all routes
	// Secure flag added when on HTTPS to prevent cookie transmission over HTTP
	const secureFlag = isSecure ? "; Secure" : "";
	document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${secureFlag}`;
}

export function getSystemTheme(): "light" | "dark" {
	if (typeof window === "undefined") {
		return "dark";
	}
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

export function getThemeFromCookie(): Theme {
	const stored = getCookie(THEME_COOKIE_NAME);
	if (stored === "light" || stored === "dark" || stored === "system") {
		return stored;
	}
	return "dark"; // Default theme
}

export function resolveTheme(theme: Theme): "light" | "dark" {
	return theme === "system" ? getSystemTheme() : theme;
}
